'use client';
import { useEffect, useMemo } from 'react';

import Link from 'next/link';

import { Chevron } from '@/modules/common/components/icons';

export interface BreadcrumbItem {
  label: string;
  clickAction?: () => void;
  link?: string;
}

type BreadcrumbsProps = {
  breadcrumbs: BreadcrumbItem[];
  basePath?: string;
} & React.HTMLAttributes<HTMLDivElement>;

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  className,
  breadcrumbs,
  basePath = 'https://www.designshop.com',
}) => {
  // Memoize breadcrumb schema for stable dependencies in useEffect
  const breadCrumbSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        return {
          '@type': 'ListItem',
          position: index + 1,
          name: item.label,
          item: isLast ? `${basePath}${item.link || ''}` : `${basePath}${item.link || ''}`,
        };
      }),
    }),
    [breadcrumbs, basePath]
  );

  // Inject the breadcrumb schema into the page
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify(breadCrumbSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [breadCrumbSchema]);

  return (
    <nav className={className} aria-label="breadcrumb">
      <ol className="flex flex-wrap items-center">
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const { label, link, clickAction } = breadcrumb;

          return (
            <li key={index} className="flex items-center overflow-hidden text-gray-800">
              {isLast ?
                <span
                  style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}
                >
                  {label}
                </span>
              : clickAction ?
                <button onClick={clickAction} className="whitespace-nowrap hover:underline">
                  {label}
                </button>
              : link ?
                <Link href={link} className="whitespace-nowrap hover:underline">
                  <span>{label}</span>
                </Link>
              : <span className="whitespace-nowrap">{label}</span>}
              {!isLast && (
                <div className="ml-[2px] flex items-center justify-center">
                  <Chevron position="right" className="mx-[7px] h-[12px]" />
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
